<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.leave_list') }} Calendar
      </oxd-text>

      <div class="orangehrm-calendar-header">
        <oxd-button display-type="text" :label="'<'" @click="previousMonth" />
        <oxd-text tag="h6" class="orangehrm-calendar-month">
          {{ currentMonthYear }}
        </oxd-text>
        <oxd-button display-type="text" :label="'>'" @click="nextMonth" />
        <oxd-button
          class="orangehrm-calendar-today"
          display-type="secondary"
          :label="$t('general.today')"
          @click="goToToday"
        />
      </div>

      <div class="orangehrm-calendar">
        <div class="orangehrm-calendar-weekdays">
          <div
            v-for="day in weekDays"
            :key="day"
            class="orangehrm-calendar-weekday"
          >
            {{ day }}
          </div>
        </div>

        <div class="orangehrm-calendar-days">
          <div
            v-for="(day, index) in calendarDays"
            :key="index"
            class="orangehrm-calendar-day"
            :class="{
              'orangehrm-calendar-day--other-month': !day.isCurrentMonth,
              'orangehrm-calendar-day--today': day.isToday,
            }"
          >
            <div class="orangehrm-calendar-day-number">
              {{ day.date.getDate() }}
            </div>
            <div class="orangehrm-calendar-day-leaves">
              <div
                v-for="leave in day.leaves"
                :key="leave.id"
                class="orangehrm-calendar-leave"
                :class="`orangehrm-calendar-leave--status-${leave.status}`"
                :title="`${leave.employee.firstName} ${leave.employee.lastName} - ${leave.leaveType.name}`"
              >
                <oxd-text tag="span" class="orangehrm-calendar-leave-text">
                  {{ leave.employee.firstName }} {{ leave.employee.lastName }}
                </oxd-text>
              </div>
              <div
                v-for="holiday in day.holidays"
                :key="`holiday-${holiday.id}`"
                class="orangehrm-calendar-holiday"
                :title="holiday.name"
              >
                <oxd-text tag="span" class="orangehrm-calendar-holiday-text">
                  🏖️ {{ holiday.name }}
                </oxd-text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="orangehrm-calendar-legend">
        <div class="orangehrm-calendar-legend-item">
          <div
            class="orangehrm-calendar-legend-color orangehrm-calendar-legend-color--pending"
          ></div>
          <oxd-text tag="span">{{ $t('leave.pending_approval') }}</oxd-text>
        </div>
        <div class="orangehrm-calendar-legend-item">
          <div
            class="orangehrm-calendar-legend-color orangehrm-calendar-legend-color--approved"
          ></div>
          <oxd-text tag="span">{{ $t('leave.scheduled') }}</oxd-text>
        </div>
        <div class="orangehrm-calendar-legend-item">
          <div
            class="orangehrm-calendar-legend-color orangehrm-calendar-legend-color--taken"
          ></div>
          <oxd-text tag="span">{{ $t('leave.taken') }}</oxd-text>
        </div>
        <div class="orangehrm-calendar-legend-item">
          <div
            class="orangehrm-calendar-legend-color orangehrm-calendar-legend-color--holiday"
          ></div>
          <oxd-text tag="span">{{ $t('leave.holidays') }}</oxd-text>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, computed, onMounted} from 'vue';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'LeaveCalendar',

  setup() {
    const currentDate = ref(new Date());
    const leaves = ref([]);
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/calendar',
    );

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const currentMonthYear = computed(() => {
      return currentDate.value.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    });

    const calendarDays = computed(() => {
      const year = currentDate.value.getFullYear();
      const month = currentDate.value.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Previous month days
      const firstDayOfWeek = firstDay.getDay();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = new Date(year, month, -i);
        // Format date as YYYY-MM-DD in local timezone (not UTC)
        const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const dayLeaves = leaves.value.filter(
          (item) => item.type === 'leave' && item.date === dateStr,
        );
        const dayHolidays = leaves.value.filter(
          (item) => item.type === 'holiday' && item.date === dateStr,
        );

        days.push({
          date,
          isCurrentMonth: false,
          isToday: false,
          leaves: dayLeaves,
          holidays: dayHolidays,
        });
      }

      // Current month days
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(year, month, i);
        const monthStr = (month + 1).toString().padStart(2, '0');
        const dayStr = i.toString().padStart(2, '0');
        const dateStr = `${year}-${monthStr}-${dayStr}`;
        const dayLeaves = leaves.value.filter(
          (item) => item.type === 'leave' && item.date === dateStr,
        );
        const dayHolidays = leaves.value.filter(
          (item) => item.type === 'holiday' && item.date === dateStr,
        );

        days.push({
          date,
          isCurrentMonth: true,
          isToday: date.getTime() === today.getTime(),
          leaves: dayLeaves,
          holidays: dayHolidays,
        });
      }

      // Next month days
      const remainingDays = 42 - days.length; // 6 weeks * 7 days
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        // Format date as YYYY-MM-DD in local timezone (not UTC)
        const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const dayLeaves = leaves.value.filter(
          (item) => item.type === 'leave' && item.date === dateStr,
        );
        const dayHolidays = leaves.value.filter(
          (item) => item.type === 'holiday' && item.date === dateStr,
        );

        days.push({
          date,
          isCurrentMonth: false,
          isToday: false,
          leaves: dayLeaves,
          holidays: dayHolidays,
        });
      }

      return days;
    });

    const fetchLeaves = async () => {
      const year = currentDate.value.getFullYear();
      const month = currentDate.value.getMonth() + 1;

      try {
        const response = await http.getAll({year, month});
        leaves.value = response.data.data;
      } catch (error) {
        console.error('Error fetching leaves:', error);
      }
    };

    const previousMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() - 1,
        1,
      );
      fetchLeaves();
    };

    const nextMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() + 1,
        1,
      );
      fetchLeaves();
    };

    const goToToday = () => {
      currentDate.value = new Date();
      fetchLeaves();
    };

    onMounted(() => {
      fetchLeaves();
    });

    return {
      currentDate,
      currentMonthYear,
      weekDays,
      calendarDays,
      previousMonth,
      nextMonth,
      goToToday,
    };
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-calendar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.orangehrm-calendar-month {
  min-width: 200px;
  text-align: center;
}

.orangehrm-calendar-today {
  margin-left: auto;
}

.orangehrm-calendar {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.orangehrm-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f5f5f5;
  border-bottom: 1px solid #d9d9d9;
}

.orangehrm-calendar-weekday {
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.orangehrm-calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: minmax(100px, auto);
}

.orangehrm-calendar-day {
  border: 1px solid #e9e9e9;
  padding: 0.5rem;
  min-height: 100px;
  background-color: #fff;
  overflow: hidden;

  &--other-month {
    background-color: #fafafa;
    opacity: 0.5;
  }

  &--today {
    background-color: #fff7e6;
    border-color: #ffa940;
  }
}

.orangehrm-calendar-day-number {
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.orangehrm-calendar-day-leaves {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.orangehrm-calendar-leave {
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &--status-1 {
    background-color: #fff7e6;
    border-left: 3px solid #ffa940;
  }

  &--status-2 {
    background-color: #e6f7ff;
    border-left: 3px solid #1890ff;
  }

  &--status-3 {
    background-color: #f6ffed;
    border-left: 3px solid #52c41a;
  }
}

.orangehrm-calendar-leave-text {
  font-size: 0.75rem;
}

.orangehrm-calendar-holiday {
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  background-color: #f0f8ff;
  border-left: 3px solid #4169e1;
  margin-top: 0.25rem;
}

.orangehrm-calendar-holiday-text {
  font-size: 0.75rem;
  font-weight: 600;
}

.orangehrm-calendar-legend {
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
  justify-content: center;
}

.orangehrm-calendar-legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.orangehrm-calendar-legend-color {
  width: 20px;
  height: 20px;
  border-radius: 3px;

  &--pending {
    background-color: #ffa940;
  }

  &--approved {
    background-color: #1890ff;
  }

  &--taken {
    background-color: #52c41a;
  }

  &--holiday {
    background-color: #4169e1;
  }
}
</style>
